import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  Center,
  createStyles,
  Group,
  LoadingOverlay,
  Menu,
  ScrollArea,
  Select,
  SimpleGrid,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons";
import { Manager_REPORT } from "apollo/queries";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useEffect, useState } from "react";
import { formatLocalDate, formatNumber } from "utiles/url";
import * as XLSX from "xlsx";
import ProductFilter from "./product";
import RetailerFilter from "./retailer";

const headers = [
  "Product Name",
  "Quantity",
  "Subtotal",
  "Warehouse",
  "Retailer",
  "Date",
];

const useStyles = createStyles((theme) => ({
  th: {
    padding: "0 !important",
  },
  control: {
    width: "100%",
    padding: `${theme.spacing.xs}px ${theme.spacing.md}px`,
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },
  icon: {
    width: 15,
    height: 21,
    borderRadius: 21,
  },
  thh: {
    color: "#666666",
    fontFamily: "'__Inter_aaf875','__Inter_Fallback_aaf875'",
    fontSize: "10px",
    textTransform: "uppercase",
    fontWeight: "bold",
  },
}));

function Th({ children, sortable, sorted, reversed, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th className={classes.th}>
      <UnstyledButton
        onClick={sortable ? onSort : null}
        className={classes.control}
      >
        <Group position="apart" spacing={5}>
          <Text weight={500} size="sm">
            {children}
          </Text>
          {sortable && (
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          )}
        </Group>
      </UnstyledButton>
    </th>
  );
}

const ManagerSalesReport = () => {
  const [size, setSize] = useState("50");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [product, setSetProduct] = useState(null);
  const [retailer, setSetRetailer] = useState(null);
  const [warehouse, setSetWarehouse] = useState(null);
  const [timeRange, setTimeRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [sortedData, setSortedData] = useState([]);
  const { loading, data, error, refetch } = useQuery(Manager_REPORT, {
    variables: {
      first: parseInt(size),
      page: activePage,
      period: timeRange || undefined,
      startDate: selectedStartDate
        ? formatLocalDate(selectedStartDate)
        : undefined,
      endDate: selectedEndDate ? formatLocalDate(selectedEndDate) : undefined,
      warehouse: warehouse || undefined,
      product: product || undefined,
      retailer: retailer || undefined,
      orderBy: undefined,
    },
    skip: !size || !activePage,
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message: error?.graphQLErrors?.[0]?.message || "Failed to get reports ",
      });
    },
  });

  const headerData = [
    {
      label: "Product Name",
      key: "productName",
      sortable: false,
      searchable: false,
      render: (rowData) => (
        <span style={{ width: "20px" }}>{rowData.productName}</span>
      ),
    },
    {
      label: "Quantity",
      key: "quantity",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{formatNumber(rowData.quantity)}</span>,
    },
    {
      label: "Total Price",
      key: "total_price",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{formatNumber(rowData.total_price)}</span>,
    },
    {
      label: "Warehouse",
      key: "warehouse",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{rowData.warehouse}</span>,
    },
    {
      label: "Retailer",
      key: "retailer",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{rowData.retailer}</span>,
    },
    {
      label: "Date",
      key: "date",
      sortable: false,
      searchable: false,
      render: (rowData) => <span>{rowData.date}</span>,
    },
  ];

  useEffect(() => {
    refetch();
  }, [
    size,
    activePage,
    timeRange,
    selectedStartDate,
    selectedEndDate,
    warehouse,
    retailer,
    refetch,
  ]);

  useEffect(() => {
    if (data) {
      setTotal(data.getWarehouseManagerSalesReport.paginatorInfo.lastPage);
      setSortedData(data.getWarehouseManagerSalesReport.data || []);
    }
  }, [data, size]);

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  const handleReset = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSetProduct(null);
    setTimeRange(null);
    setSetRetailer(null);
    setSetWarehouse(null);
    refetch();
  };

  const handleChange = (page) => {
    setActivePage(page);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Sales Report", pageWidth / 2, 20, { align: "center" });

    let subtitle = "Date: ";
    if (selectedStartDate && selectedEndDate) {
      const startDate = formatLocalDate(selectedStartDate);
      const endDate = formatLocalDate(selectedEndDate);
      subtitle += `${startDate} - ${endDate}`;
    } else {
      subtitle += "All Date";
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(subtitle, pageWidth / 2, 28, { align: "center" });

    const bodyData = data?.getWarehouseManagerSalesReport.data.map((row) => [
      row.productName || "N/A",
      row.quantity || 0,
      formatNumber(row.total_price) || 0,
      row.warehouse || "N/A",
      row.retailer || "N/A",
      row.date || "N/A",
    ]);

    doc.autoTable({
      startY: 35,
      head: [headers],
      body: bodyData,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [27, 145, 223], textColor: [255, 255, 255] },
    });

    const finalY = doc.lastAutoTable.finalY || 10;
    doc.text(
      `Total Price: ${formatNumber(
        data.getWarehouseManagerSalesReport.total_price,
      )}`,
      pageWidth - 20,
      finalY + 10,
      { align: "right" },
    );

    const exportedDate = `Exported Date: ${new Date().toLocaleString("en-US", {
      timeZone: "EAT",
    })}`;
    doc.setFontSize(10);
    doc.text(
      exportedDate,
      pageWidth - 14,
      doc.internal.pageSize.getHeight() - 10,
      { align: "right" },
    );

    doc.save("sales_report.pdf");
  };

  const exportToExcel = () => {
    const bodyData = data?.getWarehouseManagerSalesReport.data.map((row) => ({
      [headers[0]]: row.productName || "N/A",
      [headers[1]]: row.quantity || 0,
      [headers[2]]: formatNumber(row.total_price) || 0,
      [headers[3]]: row.warehouse || "N/A",
      [headers[4]]: row.retailer || "N/A",
      [headers[5]]: row.date || "N/A",
    }));

    bodyData.push({
      [headers[0]]: "Total Price",
      [headers[2]]: formatNumber(
        data?.getWarehouseManagerSalesReport.total_price,
      ),
      [headers[1]]: "",
      [headers[3]]: "",
      [headers[4]]: "",
      [headers[5]]: "",
    });

    const worksheet = XLSX.utils.json_to_sheet(bodyData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales");
    XLSX.writeFile(workbook, "sales_report.xlsx");
  };

  return (
    <div style={{ width: "98%", margin: "auto" }}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />

      <div>
        <SimpleGrid cols={6}>
          <div>
            <Select
              data={[
                { value: "DAILY", label: "Daily" },
                { value: "WEEKLY", label: "Weekly" },
                { value: "MONTHLY", label: "Monthly" },
                { value: "QUARTERLY", label: "Half Year" },
                { value: "YEARLY", label: "Yearly" },
              ]}
              value={timeRange}
              onChange={(value) => setTimeRange(value)}
              label="Select Period"
              placeholder="Select Range"
              withinPortal
              clearable
            />
          </div>
          <div>
            <DatePicker
              value={selectedStartDate}
              onChange={setSelectedStartDate}
              placeholder="Pick a date"
              label="Select Start Date"
              clearable
            />
          </div>
          <div>
            <DatePicker
              value={selectedEndDate}
              onChange={setSelectedEndDate}
              placeholder="Pick a date"
              label="Select End Date"
              clearable
            />
          </div>
          <div>
            <ProductFilter product={product} onCardClick={setSetProduct} />
          </div>
          <div>
            <RetailerFilter retailer={retailer} onCardClick={setSetRetailer} />
          </div>
        </SimpleGrid>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "10px",
            gap: "10px",
          }}
        >
          {(timeRange ||
            selectedEndDate ||
            selectedStartDate ||
            warehouse ||
            product ||
            retailer) && (
            <>
              <Button
                onClick={handleReset}
                style={{
                  width: "80px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
              >
                Cancel
              </Button>
            </>
          )}

          <Menu shadow="md" trigger="hover" openDelay={100} closeDelay={400}>
            <Menu.Target>
              <Button
                style={{
                  width: "80px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
              >
                Export
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item onClick={exportToPDF}>PDF</Menu.Item>
              <Menu.Item onClick={exportToExcel}>Excel</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>
      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            data={data ? data.getWarehouseManagerSalesReport.data : []}
            size={size}
            searchInput={false}
            handlePageSizeChange={handlePageSizeChange}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              flexDirection: "column",
              p: 1,
              gap: 1,
              m: 1,
              bgcolor: "background.paper",
              borderRadius: 1,
            }}
          >
            <div
              style={{
                marginRight: "35px",
                fontWeight: "bold",
                fontSize: "15px",
              }}
            >
              Total Price:{" "}
              {formatNumber(data?.getWarehouseManagerSalesReport.total_price)}
            </div>
          </Box>
        </ScrollArea>
      </Card>
    </div>
  );
};

export default ManagerSalesReport;
